// PO follow-up (N3): what's next from the vendor on each open PO, what they've said, and whether someone should chase
// it. The same for the PO list's columns and its "Needs follow-up" filter; plain functions over synced rows, so
// server tests check them (server/test/po-followup.test.ts). No nudge email here: that comes later, with its own send path.
import type { GmailAccount, Po, PoEmail, PoEmailReply, PoLine } from "./db.gen.ts";
import { isOpen } from "./po.ts";
import { gmailThreadUrl } from "./po-email.ts";
import { outstanding } from "./receiving.ts";

/** Days without an answer (no reply, no acknowledgement) before a sent PO needs a nudge. */
export const NO_ANSWER_DAYS = 3;
const DAY_MS = 86_400_000;

type FollowPo = Pick<Po, "id" | "status" | "sent_at" | "acknowledged_at" | "expected_ship" | "expected_arrive" | "historical">;
/** A PO line, with what receiving needs to tell what's still coming (backers' units, a closed HHL share). */
type FollowLine = Pick<PoLine, "po_id" | "qty" | "qty_received" | "promised_date"> & { qty_direct?: number | null; hhl_closed?: boolean | null };
type FollowEmail = Pick<PoEmail, "po_id" | "status" | "error" | "sent_at" | "started_at" | "from_email" | "gmail_thread_id" | "account_id">;
type FollowReply = Pick<PoEmailReply, "po_id" | "po_email_id" | "received_at" | "gmail_thread_id" | "from_name" | "from_email" | "snippet">;

/**
 * The next date the vendor owes: shipping it (before it ships), then its arrival (the earliest open line's promise, else
 * Needed by); an arrival already earlier than the ship date comes first (it's overdue however it ships).
 */
export type NextDue = { kind: "ship" | "arrive"; date: string; daysLate: number };

export type VendorState =
  | { kind: "acknowledged"; at: string }
  | { kind: "replied"; at: string; who: string; snippet: string; url: string | null }
  | { kind: "no_answer"; since: string; days: number; url: string | null }
  /** Marked as sent outside HHL Ops' Gmail: replies can't be seen, only an acknowledgement. */
  | { kind: "not_acknowledged"; since: string; days: number }
  | { kind: "none" };

export type EmailProblem = { kind: "failed" | "unsure" | "reconnect"; message: string };

export type FollowUp = { nextDue: NextDue | null; vendor: VendorState; problem: EmailProblem | null; needs: boolean; reasons: string[] };

const day = (v: string | null | undefined) => (v ? String(v).slice(0, 10) : null);
const daysBetween = (from: string, to: string) => Math.round((Date.parse(to) - Date.parse(from)) / DAY_MS);
const ms = (v: string | null | undefined) => (v ? new Date(v).getTime() : 0);

/** When an open PO's goods are expected at HHL: the earliest open line's promised date, else its Needed by; null for neither. */
export function arrivalDate(po: Pick<Po, "expected_arrive">, lines: readonly FollowLine[]): string | null {
  const promised = lines
    .filter((l) => outstanding(l) > 0 && l.promised_date)
    .map((l) => day(l.promised_date) as string)
    .sort()[0];
  return promised ?? day(po.expected_arrive);
}

/** Rows grouped by PO id, for followUp() over many POs. */
export function byPo<T extends { po_id: string }>(rows: readonly T[]): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const r of rows) out.set(r.po_id, [...(out.get(r.po_id) ?? []), r]);
  return out;
}

/**
 * One PO's follow-up as of `now` (an ISO timestamp; `today` its day at HHL). Only open POs that went to the vendor
 * (sent and not received, closed or cancelled; never drafts) can need following up. A PO recorded from paper
 * (`historical`) has its dates followed but no "no answer": nothing here knows how it was sent.
 */
export function followUp(
  po: FollowPo,
  data: {
    lines: readonly FollowLine[];
    emails: readonly FollowEmail[];
    replies: readonly FollowReply[];
    accounts?: readonly Pick<GmailAccount, "id" | "status">[];
  },
  now: string,
  today: string,
): FollowUp {
  const live = isOpen(po.status) && po.status !== "draft";
  // Next due: the ship date until it ships (an in-production PO too), then when it arrives; whichever is earlier.
  let nextDue: NextDue | null = null;
  if (live) {
    const ship = po.status === "shipped" ? null : day(po.expected_ship);
    const arrive = arrivalDate(po, data.lines);
    const next = ship && (!arrive || ship <= arrive) ? { kind: "ship" as const, date: ship } : arrive ? { kind: "arrive" as const, date: arrive } : null;
    if (next) nextDue = { ...next, daysLate: Math.max(0, daysBetween(next.date, today)) };
  }

  // The vendor's side: acknowledged, else their latest reply after the last email sent, else how long since it went.
  const sent = data.emails.filter((e) => e.status === "sent").sort((a, b) => ms(b.sent_at) - ms(a.sent_at));
  const lastSent = sent[0];
  const reply = data.replies.slice().sort((a, b) => ms(b.received_at) - ms(a.received_at))[0];
  const replyMailbox = (reply && (data.emails.find((e) => e.gmail_thread_id === reply.gmail_thread_id)?.from_email ?? lastSent?.from_email)) || "";
  let vendor: VendorState = { kind: "none" };
  // Moved back to Sent (an acknowledgement undone) it isn't acknowledged, whatever acknowledged_at still says.
  if (po.acknowledged_at && po.status !== "sent") vendor = { kind: "acknowledged", at: po.acknowledged_at };
  else if (po.status === "draft" || !po.sent_at || (po.historical && !lastSent)) vendor = { kind: "none" };
  else if (reply && (!lastSent || ms(reply.received_at) >= ms(lastSent.sent_at)))
    vendor = {
      kind: "replied",
      at: reply.received_at,
      who: reply.from_name || reply.from_email,
      snippet: reply.snippet,
      url: replyMailbox && reply.gmail_thread_id ? gmailThreadUrl(replyMailbox, reply.gmail_thread_id) : null,
    };
  else if (lastSent?.sent_at)
    vendor = {
      kind: "no_answer",
      since: lastSent.sent_at,
      days: Math.floor((ms(now) - ms(lastSent.sent_at)) / DAY_MS),
      url: lastSent.gmail_thread_id ? gmailThreadUrl(lastSent.from_email, lastSent.gmail_thread_id) : null,
    };
  else vendor = { kind: "not_acknowledged", since: po.sent_at, days: Math.floor((ms(now) - ms(po.sent_at)) / DAY_MS) };
  // In production or shipped: the vendor answered by doing it, so "no answer" is nothing to show.
  if (po.status !== "sent" && (vendor.kind === "no_answer" || vendor.kind === "not_acknowledged")) vendor = { kind: "none" };

  // Email problems: the last email failed, or may or may not have gone (until it's sent again, or the PO is marked sent
  // after it), or its mailbox needs reconnecting.
  const last = data.emails.slice().sort((a, b) => ms(b.started_at) - ms(a.started_at))[0];
  const settled = !!last && ms(po.sent_at) > ms(last.started_at);
  let problem: EmailProblem | null = null;
  if (last?.status === "failed" && !settled) problem = { kind: "failed", message: `The last email didn't go${last.error ? `: ${last.error}` : ""}` };
  else if (last?.status === "sending" && last.error && !settled)
    problem = { kind: "unsure", message: `We can't tell whether the last email went: check Sent in ${last.from_email}` };
  else if (last && data.accounts?.find((a) => a.id === last.account_id)?.status === "needs_reconnect")
    problem = { kind: "reconnect", message: `${last.from_email} needs reconnecting (Settings → Email), so replies aren't being read` };

  const reasons: string[] = [];
  if (live) {
    // Only a PO still waiting on the vendor's answer (sent): in production or shipped, they've answered by doing it.
    const waiting = po.status === "sent";
    if (waiting && vendor.kind === "replied") reasons.push("They replied: read it, then mark it acknowledged");
    if (waiting && (vendor.kind === "no_answer" || vendor.kind === "not_acknowledged") && vendor.days >= NO_ANSWER_DAYS)
      reasons.push(`No answer in ${vendor.days} days`);
    if (nextDue?.daysLate)
      reasons.push(
        `${nextDue.kind === "ship" ? "Should have shipped" : "Should have arrived"} ${nextDue.daysLate} day${nextDue.daysLate === 1 ? "" : "s"} ago`,
      );
    if (problem) reasons.push(problem.message);
  }
  return { nextDue, vendor, problem, needs: reasons.length > 0, reasons };
}
